import catalogNodeApi from "../../services/catalogNodes";
import variationGroupAPi from "../../services/variationGroups";
import articleApi from "../../services/article";

function defaultState() {
  return {
    masterData: {},
    invariantAttributes: {},
    multiArticleVariantAttributes: {},
    mediaAssets: {},
    articlePrices: {},
    priceSettings: {},
    paging: {
      articles: {
        pagesize: 5,
        pageNumber: 0
      }
    },
    metadata: {
      loading: true,
      update: {
        seen: true
      },
      node: {
        nodeId: "",
        catalogId: ""
      }
    },
    modified: {
      masterData: false,
      invariantAttributes: false,
      multiArticleVariantAttributes: false,
      mediaAssets: false,
      articlePrices: false,
      priceAttributes: false
    },
    updateTasks: {
      masterData: [],
      invariantAttributes: [],
      multiArticleVariantAttributes: [],
      mediaAssets: [],
      articlePrices: [],
      priceAttributes: []
    },
    updateErrors: {
      masterData: [],
      invariantAttributes: [],
      multiArticleVariantAttributes: [],
      mediaAssets: [],
      articlePrices: [],
      priceAttributes: []
    }
  };
}
export default {
  namespaced: true,
  state: defaultState(),
  getters: {
    isSaving(state) {
      return Object.keys(state.updateTasks).some((x) => !!state.updateTasks[x].length);
    },
    hasUnsavedData(state) {
      const sections = Object.keys(state.modified);
      return sections.some((x) => state.modified[x]);
    },
    lastRunSuccess(state) {
      return Object.keys(state.updateErrors).every((x) => !state.updateErrors[x].length);
    },
    priceQuantityAttribute(state, getters, rootState) {
      if (!state.priceSettings.quantityAttributeId) return null;
      if (!Object.keys(state.priceAttributes).length) return null;

      const language = rootState.metadata.contentLanguages.enabled[0];
      if (!language) return null;

      const item = state.priceAttributes[language.languageCode]?.[state.priceSettings?.quantityAttributeId];
      if (!item) return null;

      const fallback = language.fallbackLanguageCode ? state.priceAttributes[language.fallbackLanguageCode]?.[state.priceSettings.quantityAttributeId] : null;
      const data = {};
      data[language.languageCode] = item;

      if (fallback) data[language.fallbackLanguageCode] = fallback;

      return data;
    },
    unitQuantityAttribute(state, getters, rootState) {
      if (!state.priceSettings.unitAttributeId) return null;
      if (!Object.keys(state.priceAttributes).length) return null;

      const language = rootState.metadata.contentLanguages.enabled[0];
      if (!language) return null;

      const item = state.priceAttributes[language.languageCode]?.[state.priceSettings.unitAttributeId];
      if (!item) return null;

      const fallback = language.fallbackLanguageCode ? state.priceAttributes[language.fallbackLanguageCode]?.[state.priceSettings.unitAttributeId] : null;
      const data = {};
      data[language.languageCode] = item;

      if (fallback) data[language.fallbackLanguageCode] = fallback;

      return data;
    }
  },
  mutations: {
    "refresh-masterdata"(state, arg) {
      const data = Object.assign({}, state.masterData);
      data[arg.language] = arg.data;
      state.masterData = data;
    },
    "refresh-article-media-assets"(state, arg) {
      state.mediaAssets = arg;
    },
    "change-node"(state, args) {
      state.metadata.node = args;
    },
    "refresh-article-invariant-attributes"(state, arg) {
      state.invariantAttributes = arg;
    },
    "refresh-multi-article-variant-attributes"(state, arg) {
      state.multiArticleVariantAttributes = arg;
    },
    "refresh-article-prices"(state, prices) {
      state.articlePrices = prices;
    },
    "refresh-price-settings"(state, priceinfo) {
      state.priceSettings = { unitAttributeId: priceinfo.orderUnitId, quantityAttributeId: priceinfo.priceQuantityId };
    },
    "refresh-article-price-attributes"(state, args) {
      state.priceAttributes = args;
    },
    "update-loading-state"(state, isLoading) {
      state.metadata.loading = isLoading;
    },
    "update-save-status"(state, arg) {
      const section = state.updateTasks[arg.section];
      if (arg.running && section.indexOf(arg.lang) < 0) section.push(arg.lang);
      if (!arg.running) {
        const x = section.indexOf(arg.lang);
        section.splice(x, 1);
      }
      state.metadata.update.seen = arg.seen == undefined ? false : arg.seen;
      if (arg.error) {
        state.updateErrors[arg.section].push(arg.error);
        state.metadata.update.apiError = arg.apiError;
      }
      if (arg.success != undefined) state.metadata.update.success = arg.success;
    },
    "notification-seen"(state) {
      state.metadata.update.seen = true;
    },
    "mark-dirty-status"(state, args) {
      const data = { ...state.modified };
      data[args.section] = args.dirty;
      state.modified = data;
    },
    "clear-dirty-status"(state) {
      const defaultTemplate = defaultState();
      state.modified = defaultTemplate.modified;
    },
    "reset-state"(state) {
      Object.assign(state, defaultState());
    },
    "reset-update-status"(state) {
      const defaultTemplate = defaultState();
      state.updateTasks = defaultTemplate.updateTasks;
      state.updateErrors = defaultTemplate.updateErrors;
    },
    "update-article-paging"(state, args) {
      state.paging.articles.pagesize = args.pageSize;
      state.paging.articles.pageNumber = args.pageNumber;
    }
  },
  actions: {
    loadMasterData: async function (context, args) {
      const data = await catalogNodeApi.getMasterData(args.nodeId, args.catalogId, args.language);

      context.commit("refresh-masterdata", {
        language: args.language,
        data: data
      });
    },
    loadVariationGroupWithLocalizations: async function ({ dispatch, rootState, commit }, args) {
      try {
        commit("update-loading-state", true);
        commit("reset-state");
        commit("change-node", { nodeId: args.nodeId, catalogId: args.catalogId });

        const requestedLanguages = args.languages || rootState.metadata.contentLanguages.enabled;
        let languages = requestedLanguages
          .map((x) => [x.languageCode, x.fallbackLanguageCode])
          .flat()
          .filter((x) => !!x);
        languages = Array.from(new Set(languages));
        const tasks = [];
        languages.forEach((lang) => {
          tasks.push(dispatch("loadMasterData", { nodeId: args.nodeId, catalogId: args.catalogId, language: lang }));
        });

        tasks.push(dispatch("loadArticlePriceAttributes", { variationGroupId: args.nodeId, catalogId: args.catalogId, languages: languages }));
        tasks.push(
          dispatch("loadArticleVariantAttributes", {
            variationGroupId: args.nodeId,
            catalogId: args.catalogId,
            languages: languages,
            articlePagesize: 5,
            articlePageNumber: 1,
            articlePageOffset: 0
          })
        );
        tasks.push(dispatch("loadArticleInvariantAttributes", { variationGroupId: args.nodeId, catalogId: args.catalogId, languages: languages }));
        tasks.push(dispatch("loadArticleMediaAssets", { variationGroupId: args.nodeId, catalogId: args.catalogId, languages: languages }));

        await Promise.all(tasks);
      } catch (error) {
        console.error("loadVariationGroupWithLocalizations failed:", error);
      }
      commit("update-loading-state", false);
    },
    loadCatalogGroupWithLocalizations: async function ({ dispatch, rootState, commit }, args) {
      try {
        commit("reset-state");
        commit("update-loading-state", true);
        commit("change-node", { nodeId: args.nodeId, catalogId: args.catalogId });

        const requestedLanguages = args.languages || rootState.metadata.contentLanguages.enabled;
        let languages = requestedLanguages
          .map((x) => [x.languageCode, x.fallbackCode])
          .flat()
          .filter((x) => !!x);
        languages = new Set(languages);
        const tasks = [];
        languages.forEach((lang) => {
          tasks.push(dispatch("loadMasterData", { nodeId: args.nodeId, catalogId: args.catalogId, language: lang }));
        });

        await Promise.all(tasks);
      } catch (error) {
        console.error("loadCatalogNodeGroupWithLocalizations failed:", error);
      }
      commit("update-loading-state", false);
    },
    loadArticleVariantAttributes: async function (context, args) {
      const attributes = {};
      const tasks = args.languages.map(async (lang) => {
        attributes[lang] = await variationGroupAPi.getMultiArticleVariantAttributes(args.variationGroupId, lang, args.articlePagesize, args.articlePageOffset);
      });

      await Promise.all(tasks);
      context.commit("update-article-paging", { pageSize: args.articlePagesize, pageNumber: args.articlePageNumber });
      context.commit("refresh-multi-article-variant-attributes", attributes);
      context.commit("refresh-article-prices", attributes[args.languages[0]].articlePrices.prices);
      context.commit("refresh-price-settings", attributes[args.languages[0]].articlePrices);
    },
    loadArticleInvariantAttributes: async function (context, args) {
      const attributes = {};
      const tasks = args.languages.map(async (lang) => {
        attributes[lang] = await variationGroupAPi.getInvariantAttributes(args.variationGroupId, lang);
      });

      await Promise.all(tasks);
      context.commit("refresh-article-invariant-attributes", attributes);
    },
    loadArticlePriceAttributes: async function (context, args) {
      const attributes = {};
      const tasks = args.languages.map(async (lang) => {
        attributes[lang] = await variationGroupAPi.GetPriceAttributesFromArticleUnderVariationGroup(args.variationGroupId, args.catalogId, lang);
      });
      await Promise.all(tasks);
      context.commit("refresh-article-price-attributes", attributes);
    },
    loadArticleMediaAssets: async function (context, args) {
      const mediaAssets = {};
      const tasks = args.languages.map(async (lang) => {
        mediaAssets[lang] = await variationGroupAPi.getMediaAssets(args.variationGroupId, args.catalogId, lang);
      });

      await Promise.all(tasks);
      context.commit("refresh-article-media-assets", mediaAssets);
    },
    saveMasterData: async function (context, args) {
      context.commit("refresh-masterdata", {
        language: args.language,
        data: args.model
      });
      context.commit("update-save-status", { section: "masterData", lang: args.language, running: true });
      try {
        await catalogNodeApi.updateMasterData(args.language, args.model);
        context.commit("update-save-status", { section: "masterData", lang: args.language, running: false });
      } catch (error) {
        context.commit("update-save-status", {
          section: "masterData",
          lang: args.language,
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
    },
    saveInvariantAttributes: async function (context, args) {
      context.commit("refresh-article-invariant-attributes", args.model);
      context.commit("update-save-status", { section: "invariantAttributes", running: true });
      try {
        const languages = Object.keys(args.model);
        const tasks = languages.map((lang) => articleApi.updateInvariantAttributes(lang, args.articleId, args.model[lang]));
        await Promise.all(tasks);
      } catch (error) {
        context.commit("update-save-status", { section: "invariantAttributes", running: false, error: error, apiError: error.isAxiosError, success: false });
      }
      context.commit("update-save-status", { section: "invariantAttributes", running: false });
    },
    saveMultiArticleVariantAttributes: async function (context, args) {
      context.commit("refresh-multi-article-variant-attributes", args.model);
      context.commit("update-save-status", { section: "multiArticleVariantAttributes", running: true });

      try {
        const languages = Object.keys(args.model);
        const tasks = languages.map((lang) => articleApi.updateMultiArticleVariantAttributes(lang, args.articleId, args.model[lang].attributes));
        await Promise.all(tasks);
      } catch (error) {
        context.commit("update-save-status", {
          section: "multiArticleVariantAttributes",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
      context.commit("update-save-status", { section: "multiArticleVariantAttributes", running: false });
    },
    saveMediaAssets: async function (context, args) {
      context.commit("refresh-article-media-assets", args.model);
      context.commit("update-save-status", { section: "mediaAssets", running: true });
      try {
        await variationGroupAPi.updateMediaAssetsUnderVariatonGroup(args.language, args.model[args.language]);
      } catch (error) {
        context.commit("update-save-status", { section: "mediaAssets", running: false, error: error, apiError: error.isAxiosError, success: false });
        console.error("Save article media assets failed with error", error);
      }
      context.commit("update-save-status", { section: "mediaAssets", running: false });
    },
    saveArticlePrices: async function (context, args) {
      context.commit("refresh-article-prices", args);
      context.commit("update-save-status", { section: "articlePrices", running: true });
      try {
        await variationGroupAPi.updatePricesForArticlesUnderVariatonGroup(context.state.metadata.node.nodeId, args);
      } catch (error) {
        context.commit("update-save-status", { section: "articlePrices", running: false, error: error, apiError: error.isAxiosError, success: false });
        console.error("error", error);
      }
      context.commit("update-save-status", { section: "articlePrices", running: false });
    }
  }
};
